// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import deleteWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/DeleteWishList";
import deleteWishListShare from "@insite/client-framework/Store/Pages/MyLists/Handlers/DeleteWishListShare";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import WishListCard from "@insite/content-library/Components/WishListCard";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import TableCounter from "@steiner/App/Components/Common/TableCounter";
import XngZone from "@steiner/App/Components/Common/XngZone";
import AddToCart from "@steiner/App/Components/MyLists/AddToCart";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import { SortOrderOptions } from "@steiner/MobiusOverrides/DataTable";
import DataTableBody from "@steiner/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@steiner/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead from "@steiner/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader from "@steiner/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@steiner/MobiusOverrides/DataTable/DataTableRow";
import { getFormattedDate } from "@steiner/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}
interface State {
    wishListToAction?: WishListModel;
    deleteListModalIsOpen: boolean;
    leaveListModalIsOpen: boolean;
    // XNG-Changes
    view?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        wishLists: getWishListsDataView(state, state.pages.myLists.getWishListsParameter),
        getWishListsParameter: state.pages.myLists.getWishListsParameter,
        isUpdatingCart: state.context.isUpdatingCart,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        // XNG-Changes
        allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    };
};

const mapDispatchToProps = {
    addWishListToCart,
    deleteWishList,
    deleteWishListShare,
    updateLoadParameter,
    updateWishList,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface MyListsListStyles {
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    deleteListModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    messageText?: TypographyProps;
    // XNG-Change
    notifyWrapper?: InjectableCss;
    dataTableRow?: DataTableRowProps;
    listNameDataTableCell?: DataTableCellProps;
    addProductGroupToCartButton?: ButtonPresentationProps;
    deleteLink?: LinkPresentationProps;
    view?: InjectableCss;
    viewText?: TypographyPresentationProps;
    tableViewLink?: LinkPresentationProps;
    listViewLink?: LinkPresentationProps;
    dateDateTableCell?: DataTableCellProps;
    addToCartDataTableCell?: DataTableCellProps;
    deleteProductGroupModal?: TwoButtonModalStyles;
    leaveProductGroupModal?: TwoButtonModalStyles;
}

export const listStyles: MyListsListStyles = {
    deleteListModal: {
        sizeVariant: "small",
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    messageText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
};

const styles = listStyles;

class MyListsList extends React.Component<Props, State> {
    static contextType = ToasterContext;
    declare context: React.ContextType<typeof ToasterContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            deleteListModalIsOpen: false,
            leaveListModalIsOpen: false,
            // XNG-Changes
            view: "list",
        };
    }
    componentDidMount() {
        if (typeof window !== "undefined") {
            const storedView = window.localStorage.getItem("myListsViewMode");
            if (storedView) {
                this.setState({ view: storedView });
            } else {
                window.localStorage.setItem("myListsViewMode", "list");
            }
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (typeof window !== "undefined" && this.state.view && this.state.view !== prevState.view) {
            window.localStorage.setItem("myListsViewMode", this.state.view);
        }
    }

    deleteButtonClickHandler = (wishList: WishListModel) => {
        this.setState({
            wishListToAction: wishList,
            deleteListModalIsOpen: true,
        });
    };

    deleteCancelHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
    };

    deleteSubmitHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
        if (this.state.wishListToAction) {
            this.props.deleteWishList({
                wishListId: this.state.wishListToAction.id,
                onSuccess: this.onDeleteSuccess,
                onComplete(resultProps) {
                    if (!resultProps.error) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    onDeleteSuccess = () => {
        this.context.addToast({
            body: translate("List Deleted"),
            messageType: "success",
        });
    };

    leaveButtonClickHandler = (wishList: WishListModel) => {
        this.setState({
            wishListToAction: wishList,
            leaveListModalIsOpen: true,
        });
    };

    leaveCancelHandler = () => {
        this.setState({ leaveListModalIsOpen: false });
    };

    leaveSubmitHandler = () => {
        this.setState({ leaveListModalIsOpen: false });
        if (this.state.wishListToAction) {
            this.props.deleteWishListShare({
                wishList: this.state.wishListToAction,
                onSuccess: this.onLeaveSuccess,
                onComplete() {
                    this.onSuccess?.();
                },
            });
        }
    };

    onLeaveSuccess = () => {
        this.context.addToast({
            body: translate("You left the list"),
            messageType: "success",
        });
    };

    toggleFavoriteClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };
    toggleHideClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    // XNG-Changes
    headerClick = (sortField: string) => {
        const sort = this.props.getWishListsParameter.sort === sortField ? `${sortField} DESC` : sortField;
        this.props.updateLoadParameter({ sort });
    };

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (this.props.getWishListsParameter.sort === sortField) {
            sorted = "ascending";
        } else if (this.props.getWishListsParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    render() {
        if (!this.props.wishLists.value || this.props.wishLists.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} data-test-selector="myLists_spinner"></LoadingSpinner>
                </StyledWrapper>
            );
        }

        if (this.props.wishLists.value.length === 0) {
            if (this.props.getWishListsParameter.page && this.props.getWishListsParameter.page > 1) {
                this.props.updateLoadParameter({ page: this.props.getWishListsParameter.page - 1 });
                return null;
            }
            return (
                <StyledWrapper {...styles.notifyWrapper} data-test-selector="myLists_noListsFound">
                    <Typography {...styles.messageText} data-test-selector="myLists_noListsFound">
                        {this.props.getWishListsParameter.query
                            ? siteMessage("Lists_NoResultsMessage")
                            : siteMessage("Lists_NoListsFound")}
                    </Typography>
                </StyledWrapper>
            );
        }

        return (
            <>
                <StyledWrapper {...styles.view}>
                    <Typography {...styles.viewText}>{translate("View")}</Typography>
                    <Link
                        {...(this.state.view === "list" ? { active: true } : { active: false })}
                        title={translate("List")}
                        {...styles.listViewLink}
                        onClick={() => {
                            this.setState({ view: "list" });
                        }}
                    />
                    <Link
                        {...(this.state.view === "table" ? { active: true } : { active: false })}
                        title={translate("Table")}
                        {...styles.tableViewLink}
                        onClick={() => {
                            this.setState({ view: "table" });
                        }}
                    />
                </StyledWrapper>
                <TableCounter
                    pagination={this.props.wishLists.pagination}
                    countText={translate("Product Groups")}
                    accordionOnSmallScreen={false}
                >
                    {this.state.view === "list" ? (
                        <CardList extendedStyles={styles.cardList} key="CardList-ListView">
                            {this.props.wishLists.value.map(wishList => (
                                <CardContainer key={`List_${wishList.id}`} extendedStyles={styles.cardContainer}>
                                    <WishListCard
                                        wishList={wishList}
                                        addWishListToCart={this.props.addWishListToCart}
                                        deleteWishList={() => this.deleteButtonClickHandler(wishList)}
                                        leaveWishList={() => this.leaveButtonClickHandler(wishList)}
                                        toggleFavorite={() => this.toggleFavoriteClickHandler(wishList)}
                                        toggleHide={() => this.toggleHideClickHandler(wishList)}
                                    />
                                </CardContainer>
                            ))}
                        </CardList>
                    ) : (
                        <DataTable key="CardList-TableView">
                            <DataTableHead>
                                <DataTableHeader
                                    sorted={this.sorted("Name")}
                                    onSortClick={() => this.headerClick("Name")}
                                >
                                    {translate("Product Group")}
                                </DataTableHeader>
                                <DataTableHeader>{translate("# of Items")}</DataTableHeader>
                                <DataTableHeader
                                    sorted={this.sorted("ModifiedOn")}
                                    onSortClick={() => this.headerClick("ModifiedOn")}
                                >
                                    {translate("Last Updated")}
                                </DataTableHeader>
                                <DataTableHeader>
                                    <VisuallyHidden>{translate("Add To cart")}</VisuallyHidden>
                                </DataTableHeader>
                                <DataTableHeader>
                                    <VisuallyHidden>{translate("Delete Product Group")}</VisuallyHidden>
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {this.props.wishLists.value.map(wishList => (
                                    <DataTableRow key={`Table_${wishList.id}`} {...styles.dataTableRow}>
                                        <DataTableCell {...styles.listNameDataTableCell}>
                                            <MyListsDetailsPageTypeLink
                                                title={wishList.name}
                                                wishListId={wishList.id}
                                                testSelector="wishListCardName"
                                            />
                                        </DataTableCell>
                                        <DataTableCell>{wishList.wishListLinesCount}</DataTableCell>
                                        <DataTableCell {...styles.dateDateTableCell}>
                                            {wishList.updatedOn && getFormattedDate(new Date(wishList.updatedOn))}
                                        </DataTableCell>
                                        <DataTableCell {...styles.addToCartDataTableCell}>
                                            <AddToCart
                                                disabled={
                                                    !(
                                                        wishList.canAddToCart &&
                                                        !!wishList.wishListLinesCount &&
                                                        wishList.wishListLinesCount > 0
                                                    ) || this.props.isUpdatingCart
                                                }
                                                wishListId={wishList.id}
                                                extendedStyles={styles.addProductGroupToCartButton}
                                            >
                                                {!this.props.isTabletScreen && translate("Add Product Group To Cart")}
                                            </AddToCart>
                                        </DataTableCell>
                                        <DataTableCell>
                                        {this.props.allowEditingOfWishLists &&
                                            !wishList.isSharedList &&
                                            !wishList.isAutogenerated && (
                                            <Link
                                                onClick={() => {
                                                    this.deleteButtonClickHandler(wishList);
                                                }}
                                                {...styles.deleteLink}
                                            />
                                        )}
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    )}
                </TableCounter>

                <TwoButtonModal
                    modalIsOpen={this.state.deleteListModalIsOpen}
                    headlineText={translate("Delete List")}
                    messageText={`${translate("Are you sure you want to delete")} ${
                        this.state.wishListToAction ? this.state.wishListToAction.name : ""
                    }?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="myListsListSubmitDelete"
                    extendedStyles={styles.deleteProductGroupModal}
                ></TwoButtonModal>
                <TwoButtonModal
                    modalIsOpen={this.state.leaveListModalIsOpen}
                    headlineText={translate("Leave List")}
                    messageText={`${translate("Are you sure you want to leave")} ${
                        this.state.wishListToAction ? this.state.wishListToAction.name : ""
                    }?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Leave")}
                    onCancel={this.leaveCancelHandler}
                    onSubmit={this.leaveSubmitHandler}
                    submitTestSelector="myListsListSubmitLeave"
                    extendedStyles={styles.leaveProductGroupModal}
                ></TwoButtonModal>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsList),
    definition: {
        group: "My Lists",
        displayName: "List",
        allowedContexts: ["MyListsPage"],
    },
};

export default widgetModule;
